import React, { useLayoutEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';

import PageDataLoader from 'components/pageDataLoader';
import { setUserProfileDetailsV3 } from 'actions/userManagementV2';
import {
    updateStaffStatusV3,
    getStaffProfile,
    updateStaffProfile,
    uploadStaffPhoto,
    deleteStaffPhoto,
    uploadStaffDocument,
    getStaffDocuments,
    replaceStaffDocument,
    deleteStaffDocument,
    getUserActivity,
    getAttendanceOverview,
    getStaffActivityStats,
    getStaffListV3,
    getStaffStats,
} from 'actions/userManagementV3';
import {
    getStudentProfile,
    updateStudentProfile,
    updateStudentStatus,
    uploadStudentPhoto,
    deleteStudentPhoto,
    uploadStudentDocument,
    getStudentDocuments,
    replaceStudentDocument,
    deleteStudentDocument,
    getStudentCourses,
    enrollStudentCourse,
    unassignStudentCourse,
    updateStudentBatch,
    assignStudentStandard,
    unassignStudentStandard,
    getStudentAttendanceTab,
    getTeacherAttendanceTab,
    getOperationAttendanceTab,
    getStudentFee,
    getStudentActivityStats,
} from 'actions/studentManagement';
import { getHolidays } from 'actions/profileNew';
import { attendanceRequirementDetail } from 'actions/attendanceNew';
import { fluid } from 'components/UserDashboardV2/utils/responsive';
import './styles.css';

import { TABS } from './staffProfile/constants';
import { useProfileDetails } from './staffProfile/useProfileDetails';
import { useAttendanceState } from './staffProfile/useAttendanceState';
import { useProfileTabsData } from './staffProfile/useProfileTabsData';
import ProfileHeader from './staffProfile/ProfileHeader';
import ProfileTabContent from './staffProfile/ProfileTabContent';
import ProfileModals from './staffProfile/ProfileModals';

const StaffProfilePage = (props) => {
    const {
        userRowDetails,
        attendanceOverview,
        holidays,
        storeWorkingDaysDetails,
        attendanceRequirement,
        ...actions
    } = props;

    const history = useHistory();
    const location = useLocation();
    const basePath = window.location.pathname.startsWith('/user-management-v2') ? '/user-management-v2' : '/user-management';
    const isStaffRoute = window.location.pathname.endsWith('/staff-profile');
    const userId = userRowDetails?.id;

    const [activeTab, setActiveTab] = useState(TABS[0].label);
    const [tabLoader, setTabLoader] = useState(false);

    // Profile Details Hook
    const profile = useProfileDetails({
        userId,
        userRowDetails,
        isStaffRoute,
        basePath,
        history,
        actions,
    });

    // Same "Joined" date as the Basic details sidebar — dates before it are disabled in the attendance calendar.
    const joinedDate = (isStaffRoute
        ? (profile.staffProfile?.loginAndAccount || profile.specificUserProfile?.loginAndAccount)
        : profile.studentSidebar?.loginAndAccount)?.joined || userRowDetails?.joinedOn || null;

    // Attendance Hook
    const attendance = useAttendanceState({
        userId,
        userRowDetails,
        specificUserProfile: profile.specificUserProfile,
        joinedDate,
        isStaffRoute,
        isTeacher: profile.isTeacher,
        activeTab,
        holidays,
        storeWorkingDaysDetails,
        attendanceRequirement,
        setPageLoader: profile.setPageLoader,
        actions,
    });

    // Profile Tabs Data Hook (Courses, Documents, Activity, Fees)
    const tabsData = useProfileTabsData({
        userId,
        userRowDetails,
        specificUserProfile: profile.specificUserProfile,
        isStaffRoute,
        activeTab,
        setPageLoader: profile.setPageLoader,
        onCoursesChanged: () => profile.refetchProfile(),
        actions,
    });

    // Sizing refs
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(300);
    const sidebarRef = useRef(null);
    const [sidebarHeight, setSidebarHeight] = useState(null);

    useLayoutEffect(() => {
        const measure = () => {
            if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
            if (sidebarRef.current) setSidebarHeight(sidebarRef.current.offsetHeight);
            if (attendance.attendanceSummaryCardRef.current) {
                attendance.setAttendanceSummaryHeight(attendance.attendanceSummaryCardRef.current.offsetHeight);
            }
        };
        measure();
        window.addEventListener('resize', measure);

        let resizeObserver;
        if (typeof ResizeObserver !== 'undefined' && attendance.attendanceSummaryCardRef.current) {
            resizeObserver = new ResizeObserver(() => {
                if (attendance.attendanceSummaryCardRef.current) {
                    attendance.setAttendanceSummaryHeight(attendance.attendanceSummaryCardRef.current.offsetHeight);
                }
            });
            resizeObserver.observe(attendance.attendanceSummaryCardRef.current);
        }

        return () => {
            window.removeEventListener('resize', measure);
            if (resizeObserver) resizeObserver.disconnect();
        };
    }, [profile.loading, profile.specificUserProfile, userRowDetails, activeTab, attendance.studentAttendanceData, attendance.setAttendanceSummaryHeight]);

    if (profile.loading) {
        return <PageDataLoader visible={profile.loading} />;
    }

    const bodyAreaHeight = `calc(100% - (${headerHeight}px + ${fluid(20)}))`;
    const leftColumnHeight = sidebarHeight != null ? `max(${bodyAreaHeight}, ${sidebarHeight}px)` : bodyAreaHeight;

    const handleTabClick = (label) => {
        profile.handleCancelSection();
        setActiveTab(label);
        if (label.startsWith("Courses & Batches")) tabsData.fetchCourses();
        if (label === TABS[2].label || label === "Fees" || label === "FEE") tabsData.fetchFee();
        if (label === TABS[3].label || label === "Attendance") attendance.fetchAttendance();
        if (label === TABS[4].label || label === "DOCUMENTS" || label === "Documents") tabsData.fetchDocuments();
        if (label === TABS[5].label || label === "Activity") {
            tabsData.fetchActivities();
            tabsData.fetchActivityStats();
        }
    };

    return (
        <div style={{
            width: "calc(100% + 30px)",
            margin: -20,
            padding: `${fluid(4)} ${fluid(20)} ${fluid(20)}`,
            height: "calc(100% + 40px)",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            overflow: activeTab === TABS[0].label ? "visible" : "hidden",
        }}>
            <style>{`
                .staff-profile-info-scroll::-webkit-scrollbar { display: none; }
            `}</style>

            <ProfileHeader
                headerRef={headerRef}
                isStaffRoute={isStaffRoute}
                history={history}
                location={location}
                basePath={basePath}
                specificUserProfile={profile.specificUserProfile}
                userRowDetails={userRowDetails}
                editingSection={profile.editingSection}
                setEditingSection={profile.setEditingSection}
                draftProfile={profile.draftProfile}
                setDraftProfile={profile.setDraftProfile}
                savingSection={profile.savingSection}
                handleSaveSection={profile.handleSaveSection}
                handleCancelSection={profile.handleCancelSection}
                handleToggleStatus={profile.handleToggleStatus}
                handleDelete={profile.handleDelete}
                handleEditClick={profile.handleEditClick}
                handleAvatarClick={profile.handleAvatarClick}
                avatarHovered={profile.avatarHovered}
                setAvatarHovered={profile.setAvatarHovered}
                imgUrl={profile.imgUrl}
                imageFile={profile.imageFile}
                pendingPhotoRemoval={profile.pendingPhotoRemoval}
                isActive={profile.isActive}
                status={profile.status}
                roleStr={profile.roleStr}
                activeTab={activeTab}
                handleTabClick={handleTabClick}
                // Until the Courses & Batches API has answered, show the same
                // active-courses count Basic details' quick stats uses.
                enrolledCoursesCount={tabsData.hasFetchedCourses
                    ? tabsData.enrolledCourses.length
                    : Number(
                        profile.studentSidebar?.quickStats?.activeCourses
                        ?? profile.specificUserProfile?.quickStats?.activeCourses
                        ?? 0
                    ) || 0}
            />

            <ProfileTabContent
                activeTab={activeTab}
                isStaffRoute={isStaffRoute}
                isStaff={profile.isStaff}
                isTeacher={profile.isTeacher}
                profile={{ ...profile, userRowDetails }}
                sidebarRef={sidebarRef}
                leftColumnHeight={leftColumnHeight}
                tabsData={tabsData}
                attendance={attendance}
                attendanceOverview={attendanceOverview}
                setTabLoader={setTabLoader}
                joinedDate={joinedDate}
            />

            <ProfileModals
                editOpen={profile.editOpen}
                setEditOpen={profile.setEditOpen}
                editingStaffForDrawer={profile.editingStaffForDrawer}
                refetchProfile={profile.refetchProfile}
                profilePicModalOpen={profile.profilePicModalOpen}
                setProfilePicModalOpen={profile.setProfilePicModalOpen}
                handleRemovePhotoConfirm={profile.handleRemovePhotoConfirm}
                handleProfilePicModalOk={profile.handleProfilePicModalOk}
                specificUserProfile={profile.specificUserProfile}
                existingPhotoSrc={profile.existingPhotoSrc}
                inputProfilePicRef={profile.inputProfilePicRef}
                storeImageFile={profile.storeImageFile}
                handleConfirmPhotoChoice={profile.handleConfirmPhotoChoice}
                handleProfilePicCancel={profile.handleProfilePicCancel}
                setImgUrl={profile.setStagedImgUrl}
                imgUrl={profile.stagedImgUrl}
                imageFile={profile.stagedImageFile}
                setImageFile={profile.setStagedImageFile}
                documentModal={tabsData.documentModal}
                setDocumentModal={tabsData.setDocumentModal}
                handleReplaceDocument={tabsData.handleReplaceDocument}
                handleUploadDocuments={tabsData.handleUploadDocuments}
                enrollCourseModalOpen={tabsData.enrollCourseModalOpen}
                enrolledCourses={tabsData.enrolledCourses}
                setEnrollCourseModalOpen={tabsData.setEnrollCourseModalOpen}
                handleEnrollCourse={tabsData.handleEnrollCourse}
                userId={userId}
                confirmAction={profile.confirmAction}
                confirmLoading={profile.confirmLoading}
                closeConfirmModal={profile.closeConfirmModal}
                handleConfirmAction={profile.handleConfirmAction}
                addBatchDrawerData={tabsData.addBatchDrawerData}
                setAddBatchDrawerData={tabsData.setAddBatchDrawerData}
                unassignCourseModal={tabsData.unassignCourseModal}
                setUnassignCourseModal={tabsData.setUnassignCourseModal}
                unassigningCourse={tabsData.unassigningCourse}
                handleConfirmUnassignCourse={tabsData.handleConfirmUnassignCourse}
                changeBatchModalData={tabsData.changeBatchModalData}
                setChangeBatchModalData={tabsData.setChangeBatchModalData}
                handleChangeStudentBatch={tabsData.handleChangeStudentBatch}
                unassignBatchModal={tabsData.unassignBatchModal}
                setUnassignBatchModal={tabsData.setUnassignBatchModal}
                unassigningBatch={tabsData.unassigningBatch}
                handleConfirmUnassignBatch={tabsData.handleConfirmUnassignBatch}
                cancelRequestModal={tabsData.cancelRequestModal}
                setCancelRequestModal={tabsData.setCancelRequestModal}
                cancelingRequest={tabsData.cancelingRequest}
                handleConfirmCancelRequest={tabsData.handleConfirmCancelRequest}
                tabLoader={tabLoader}
                pageLoader={profile.pageLoader}
                enrolledCoursesLoading={tabsData.enrolledCoursesLoading}
                studentFeeLoading={tabsData.studentFeeLoading}
                userActivityLoading={tabsData.userActivityLoading}
                activityStatsLoading={tabsData.activityStatsLoading}
                documentsLoading={tabsData.documentsLoading}
            />
        </div>
    );
};

const mapStateToProps = (state) => {
    const { userRowDetails } = state.userManagementV2;
    const { attendanceOverview } = state.userManagementV3;
    const holidays = state.profileNew?.holidays;
    const storeWorkingDaysDetails = state.attendanceNew?.storeWorkingDaysDetails;
    const attendanceRequirement = state.attendanceNew?.getattendanceRequirement;
    return { userRowDetails, attendanceOverview, holidays, storeWorkingDaysDetails, attendanceRequirement };
};

const mapDispatchToProps = (dispatch) => ({
    getStaffListV3: (params, callback) => dispatch(getStaffListV3(params, callback)),
    getStaffStats: (params, callback) => dispatch(getStaffStats(params, callback)),
    updateStaffStatusV3: (userId, status, callback) => dispatch(updateStaffStatusV3(userId, status, callback)),
    getStaffProfile: (id, callback) => dispatch(getStaffProfile(id, callback)),
    updateStaffProfile: (id, params, callback) => dispatch(updateStaffProfile(id, params, callback)),
    uploadStaffPhoto: (id, file, callback) => dispatch(uploadStaffPhoto(id, file, callback)),
    deleteStaffPhoto: (id, callback) => dispatch(deleteStaffPhoto(id, callback)),
    uploadStaffDocument: (id, file, callback) => dispatch(uploadStaffDocument(id, file, callback)),
    getStaffDocuments: (id, callback) => dispatch(getStaffDocuments(id, callback)),
    replaceStaffDocument: (id, documentId, file, callback) => dispatch(replaceStaffDocument(id, documentId, file, callback)),
    deleteStaffDocument: (id, documentId, callback) => dispatch(deleteStaffDocument(id, documentId, callback)),
    setUserProfileDetailsV3: (key, val) => dispatch(setUserProfileDetailsV3(key, val)),
    getStudentProfile: (id, callback) => dispatch(getStudentProfile(id, callback)),
    updateStudentProfile: (id, params, callback) => dispatch(updateStudentProfile(id, params, callback)),
    updateStudentStatus: (userId, status, callback) => dispatch(updateStudentStatus(userId, status, callback)),
    uploadStudentPhoto: (userId, file, callback) => dispatch(uploadStudentPhoto(userId, file, callback)),
    deleteStudentPhoto: (userId, callback) => dispatch(deleteStudentPhoto(userId, callback)),
    uploadStudentDocument: (userId, file, callback) => dispatch(uploadStudentDocument(userId, file, callback)),
    getStudentDocuments: (userId, callback) => dispatch(getStudentDocuments(userId, callback)),
    replaceStudentDocument: (userId, documentId, file, callback) => dispatch(replaceStudentDocument(userId, documentId, file, callback)),
    deleteStudentDocument: (userId, documentId, callback) => dispatch(deleteStudentDocument(userId, documentId, callback)),
    getStudentCourses: (userId, callback) => dispatch(getStudentCourses(userId, callback)),
    enrollStudentCourse: (userId, params, callback) => dispatch(enrollStudentCourse(userId, params, callback)),
    unassignStudentCourse: (userId, enrollmentId, courseType, callback) => dispatch(unassignStudentCourse(userId, enrollmentId, courseType, callback)),
    updateStudentBatch: (userId, enrollmentId, params, callback) => dispatch(updateStudentBatch(userId, enrollmentId, params, callback)),
    assignStudentStandard: (params, callback) => dispatch(assignStudentStandard(params, callback)),
    unassignStudentStandard: (params, callback) => dispatch(unassignStudentStandard(params, callback)),
    getUserActivity: (params, firstTimeLoading, callback, loader) => dispatch(getUserActivity(params, firstTimeLoading, callback, loader)),
    getAttendanceOverview: (params, callback) => dispatch(getAttendanceOverview(params, callback)),
    getStudentAttendanceTab: (studentId, params, callback) => dispatch(getStudentAttendanceTab(studentId, params, callback)),
    getTeacherAttendanceTab: (teacherId, params, callback) => dispatch(getTeacherAttendanceTab(teacherId, params, callback)),
    getOperationAttendanceTab: (operatorId, params, callback) => dispatch(getOperationAttendanceTab(operatorId, params, callback)),
    getStudentFee: (userId, callback) => dispatch(getStudentFee(userId, callback)),
    getStudentActivityStats: (userId, callback) => dispatch(getStudentActivityStats(userId, callback)),
    getStaffActivityStats: (userId, callback) => dispatch(getStaffActivityStats(userId, callback)),
    getHolidays: (params, callback) => dispatch(getHolidays(params, callback)),
    attendanceRequirementDetail: () => dispatch(attendanceRequirementDetail()),
});

export default connect(mapStateToProps, mapDispatchToProps)(StaffProfilePage);
